import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Cross-instance sync: in the same tab, the browser's native "storage"
 * event does NOT fire for the tab that made the change. We need a custom
 * event to notify other hook instances in the same tab so they re-read
 * the new value. Without this, two components using the same key see
 * diverging state after one of them writes.
 */
const LOCAL_UPDATE_EVENT = "motionforge-localstorage-update";

function notifyOthers(key: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(LOCAL_UPDATE_EVENT, { detail: { key } }),
  );
}

/**
 * React hook for persisting state to localStorage.
 * Reads initial value from storage (if present), writes updates back,
 * and syncs across all hook instances with the same key in the same tab.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return initialValue;
      return JSON.parse(stored) as T;
    } catch {
      return initialValue;
    }
  });

  const keyRef = useRef(key);

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      try {
        const stored = window.localStorage.getItem(key);
        setValue(stored !== null ? (JSON.parse(stored) as T) : initialValue);
      } catch {
        setValue(initialValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Listen for updates from other hook instances (same tab) AND other
  // tabs (native storage event). Re-read from localStorage on either.
  useEffect(() => {
    const onLocalUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail as { key?: string } | undefined;
      if (detail?.key !== key) return;
      try {
        const stored = window.localStorage.getItem(key);
        setValue(stored !== null ? (JSON.parse(stored) as T) : initialValue);
      } catch {
        // ignore parse errors
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        setValue(e.newValue !== null ? (JSON.parse(e.newValue) as T) : initialValue);
      } catch {
        // ignore
      }
    };
    window.addEventListener(LOCAL_UPDATE_EVENT, onLocalUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(LOCAL_UPDATE_EVENT, onLocalUpdate);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Keep a ref to the latest value so functional updates can resolve
  // without being inside setState's callback (which may be invoked twice
  // in Strict Mode and dispatch events twice).
  const valueRef = useRef(value);
  valueRef.current = value;

  // Wrap setValue so writes propagate to other hook instances AND to
  // localStorage. Runs outside of setState's callback so the storage
  // write and dispatch happen exactly once per call.
  const setValueAndNotify = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === "function"
          ? (next as (p: T) => T)(valueRef.current)
          : next;
      try {
        window.localStorage.setItem(key, JSON.stringify(resolved));
      } catch {
        // ignore quota
      }
      setValue(resolved);
      notifyOthers(key);
    },
    [key],
  );

  return [value, setValueAndNotify];
}
